import './libs/jquery';
import type {App} from 'vue';
import type {Router} from 'vue-router';
import {showLoadingToast, showFailToast, showSuccessToast, closeToast} from 'vant';
import {DEFAULT_PRINTER_URL, printerClient} from './libs/printerClient';
import {fetchAndRender} from './libs/fetchAndRender';

/** 移动端 install 配置 */
export interface MobileInstallOptions {
  /** vue-router 实例（用于注册打印设置路由） */
  router: Router;
  /** 打印服务（WebSocket）地址；未传时使用默认地址 DEFAULT_PRINTER_URL */
  printerHost?: string;
  /** 打印设置路由路径，默认 '/printer-setting' */
  routePath?: string;
}


/**
 * 移动端打印入口（vant/x5 webview）。
 *
 * 与桌面 install 的区别：不注册设计器/预览页/hiprintVue，只
 *   1. 建立打印服务 WS 连接（printerClient 单例）
 *   2. 注册一个打印设置路由（vant 版页面：连接状态/选打印机/默认开关）
 *   3. 注入 $serverPrint 方法
 *
 * 用法：
 *   import {mobileInstall} from 'yh-hiprint/mobile';
 *   mobileInstall(app, {router, printerHost: 'ws://...'});
 *   // 任意位置 router.push('/printer-setting') 进入设置页
 *   // 注入的 $serverPrint(code, params, data) 直接打印
 */
export function mobileInstall(app: App, options: MobileInstallOptions): void {
  const {router, printerHost, routePath = '/printer-setting'} = options;
  const resolvedPrinterHost = printerHost?.trim() || DEFAULT_PRINTER_URL;

  // 1. 建立打印服务连接（printerClient 单例，与设置页共享）
  if (typeof window !== 'undefined') {
    printerClient.connect(resolvedPrinterHost);
  }

  // 2. 注册打印设置路由
  router.addRoute({
    path: routePath,
    name: 'PrinterSetting',
    component: () => import('./mobile/PrinterSetting.vue'),
  });

  if (router.hasRoute(routePath)) {
    const current = router.currentRoute.value;
    if (current.path === routePath && current.matched.length === 0 && !current.meta?.__routeHandled补救) {
      current.meta ??= {};
      (current.meta as any).__routeHandled补救 = true;
      router.replace(routePath).catch(() => {});
    }
  }

  // 4. 注入 $serverPrint
  app.provide('$serverPrint', mobileServerPrint);
  app.config.globalProperties.$serverPrint = mobileServerPrint;
}

/**
 * 移动端服务端打印。
 *
 * 获取模板 + 渲染 HTML → 通过 printerClient 单例发送到打印服务静默打印。
 * 打印机优先级：接口返回 printer_name（最高） > params.printerName > 设置页选中；都没有则 toast 提示。
 * data 通过 params.data 传递，前端打印机名通过 params.printerName 传递。
 *
 * 兼容历史签名：位置参数 (code, params)
 *
 * @returns true 成功 / false 失败（失败会 toast 提示）
 */
export async function mobileServerPrint(code: string, params: any = {}): Promise<boolean> {
  const toast = showLoadingToast({message: '正在获取打印模板', forbidClick: true, duration: 0});
  try {
    // 1. 拆出前端控制字段：data + printerName（均不发给后端）
    const data = params.data;
    const printerName = params.printerName;
    const fetchParams = {...params};
    delete fetchParams.data;
    delete fetchParams.printerName;

    // 2. 渲染 HTML（不传 onAlert，失败时 throw 由 catch 统一提示）
    const result = await fetchAndRender({code, params: fetchParams, data});
    if (!result || !result.html) {
      closeToast();
      showFailToast('打印内容渲染失败');
      return false;
    }
    if (!result.hasData) {
      closeToast();
      showFailToast('数据源没有数据，打印将取消');
      return false;
    }

    // 2. 校验连接
    if (printerClient.status.value !== 'connected') {
      closeToast();
      showFailToast('打印服务未连接，请检查打印客户端是否运行');
      return false;
    }

    // 3. 取打印机：接口 printer_name 优先级最高 > 前端 printerName > 设置页选中
    const selectedPrinter = result.printerName || printerName || printerClient.selectedPrinter.value;
    if (!selectedPrinter) {
      closeToast();
      showFailToast('未选择打印机，请到打印设置中选择');
      return false;
    }

    // 4. 发送打印任务
    toast.message = '正在发送打印任务';
    const printRes = await printerClient.printHtml({
      html: result.html,
      printerName: selectedPrinter,
      copies: 1,
      title: result.name,
      pageSize: result.pageSize,
    });

    closeToast();
    if (printRes?.success) {
      showSuccessToast('打印任务已发送');
      return true;
    }
    showFailToast('打印失败: ' + (printRes?.message || '未知错误'));
    return false;
  } catch (error) {
    closeToast();
    showFailToast((error as Error)?.message || '打印出错');
    return false;
  }
}

export default mobileInstall;
